import { useCallback } from "react";
import { axiosInstance } from "@/api/client";
import { tokenStorage } from "@/auth/tokenStorage";
import { useAuthStore, type AuthUser } from "@/auth/authStore";

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  is_superuser?: boolean;
}

export function useAuth() {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const setAuthenticating = useAuthStore((s) => s.setAuthenticating);
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated);
  const setUnauthenticated = useAuthStore((s) => s.setUnauthenticated);

  const login = useCallback(
    async (organizationCode: string, email: string, password: string) => {
      setAuthenticating();
      try {
        // AM-051-A2: وضعیت قبلی (به‌ویژه پرچم Superuser) پاک شود تا interceptor هدر قدیمی را روی لاگین نگذارد.
        tokenStorage.clear();

        // AM-051-02 گام ۳: «کد سازمان» فقط به‌عنوان ادعا همراه خودِ درخواست لاگین فرستاده می‌شود
        // تا سرور آن را با tenant کاربر تطبیق دهد (عدم تطابق => 400 TENANT_MISMATCH).
        // برای کاربر عادی چیزی در storage نگه داشته نمی‌شود.
        const code = organizationCode.trim();

        // ۱. دریافت توکن‌ها (پاسخ شامل user نیست)
        const res = await axiosInstance.post<LoginResponse>(
          "/api/v1/identity/login/",
          { email, password },
          { headers: code ? { "X-Tenant-Id": code } : undefined },
        );

        tokenStorage.setAccessToken(res.data.access_token);
        if (res.data.refresh_token) {
          tokenStorage.setRefreshToken(res.data.refresh_token);
        }

        // پرچم فقط راهنمای UX است؛ تصمیم امنیتی همیشه سمت سرور گرفته می‌شود.
        const isSuperuser = Boolean(res.data.is_superuser);
        tokenStorage.setIsSuperuser(isSuperuser);
        // فقط Superuser tenant انتخاب‌شده را برای هدر درخواست‌های بعدی نگه می‌دارد.
        tokenStorage.setTenantId(isSuperuser && code ? code : null);

        // ۲. دریافت اطلاعات کاربر
        const meRes = await axiosInstance.get<AuthUser>("/api/v1/identity/me/");
        setAuthenticated(meRes.data);

        return { ok: true as const };
      } catch (err) {
        tokenStorage.clear();
        setUnauthenticated();
        return { ok: false as const, error: err };
      }
    },
    [setAuthenticating, setAuthenticated, setUnauthenticated],
  );

  const logout = useCallback(async () => {
    try {
      const refreshToken = tokenStorage.getRefreshToken();
      if (refreshToken) {
        await axiosInstance.post("/api/v1/identity/logout/", {
          refresh_token: refreshToken,
        });
      }
    } finally {
      tokenStorage.clear();
      setUnauthenticated();
    }
  }, [setUnauthenticated]);

  return { status, user, login, logout };
}
